<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { isBrowserNotificationsEnabledForUser } from 'dashboard/helper/browserNotifications';
import { requestPushPermissions } from 'dashboard/helper/pushHelper';

const { t } = useI18n();
const store = useStore();

const isVisible = ref(false);
const isDismissed = ref(false);
const permissionState = ref(
  typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
);

const featureEnabled = computed(() =>
  isBrowserNotificationsEnabledForUser(store)
);

// Show only when:
// - the per-account feature is on for this user
// - the browser exposes the Notification API
// - permission is in 'default' (never asked) or 'denied'
// - user hasn't dismissed it in this session
const shouldShow = computed(() => {
  if (isDismissed.value) return false;
  if (!featureEnabled.value) return false;
  if (permissionState.value === 'unsupported') return false;
  if (permissionState.value === 'granted') return false;
  return true;
});

const enable = () => {
  requestPushPermissions({
    onSuccess: () => {
      permissionState.value = 'granted';
    },
  });
  // Reflect any user choice immediately, even if they deny
  setTimeout(() => {
    if (typeof Notification !== 'undefined') {
      permissionState.value = Notification.permission;
    }
  }, 500);
};

const dismiss = () => {
  isDismissed.value = true;
  isVisible.value = false;
};

onMounted(() => {
  // Slight delay to avoid showing during the auth flicker
  setTimeout(() => {
    isVisible.value = true;
  }, 800);
});
</script>

<template>
  <transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isVisible && shouldShow"
      class="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-n-weak bg-n-background shadow-lg p-4"
    >
      <div class="flex items-start gap-3">
        <span
          class="size-9 inline-flex items-center justify-center rounded-full bg-n-brand/10 text-n-brand shrink-0"
        >
          <span class="i-lucide-bell-ring size-5" />
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('PUSH_PERMISSION_BANNER.TITLE') }}
          </p>
          <p class="mt-1 text-xs text-n-slate-11">
            {{
              permissionState === 'denied'
                ? t('PUSH_PERMISSION_BANNER.DESCRIPTION_DENIED')
                : t('PUSH_PERMISSION_BANNER.DESCRIPTION_DEFAULT')
            }}
          </p>
          <div class="mt-3 flex items-center gap-2">
            <button
              v-if="permissionState !== 'denied'"
              type="button"
              class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition"
              @click="enable"
            >
              {{ t('PUSH_PERMISSION_BANNER.ENABLE') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-transparent px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1 transition"
              @click="dismiss"
            >
              {{ t('PUSH_PERMISSION_BANNER.DISMISS') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
